import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ACCOUNT_KINDS, useCreateAccount, type AccountKind } from "@/api/accounts";
import { useHousehold } from "@/app/useHousehold";
import { AmountError } from "@/money";
import { ErrorNote, Field, Panel, PageTitle, buttonClass, inputClass } from "@/components/ui";
import { violationsOf } from "@/i18n/apiMessages";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";
import { today } from "@/dates";

export function NewAccountPage() {
  const t = useT();
  const format = useFormat();
  const { householdId = "" } = useParams();
  const household = useHousehold();
  const navigate = useNavigate();
  const create = useCreateAccount(householdId);

  const [name, setName] = useState("");
  const [kind, setKind] = useState<AccountKind>("checking");
  const [opening, setOpening] = useState(() => format.plain(0, household?.base_currency_code ?? "EUR"));
  const [openingDate, setOpeningDate] = useState(today());
  const [institution, setInstitution] = useState("");
  const [amountError, setAmountError] = useState<string>();

  // A new account is denominated in the household's base currency. Foreign
  // accounts are a real case (`model.md` §6.2) but they need a rate on every
  // transaction, and the rate table arrives with K-17.
  const currency = household?.base_currency_code ?? "EUR";

  const violation = violationsOf(t, create.error);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setAmountError(undefined);

    let openingMinor: number;
    try {
      openingMinor = format.parse(opening, currency);
    } catch (err) {
      setAmountError(err instanceof AmountError ? t(err.key, err.params) : String(err));
      return;
    }

    create.mutate(
      {
        name,
        kind,
        currency_code: currency,
        opening_balance_minor: openingMinor,
        opening_date: openingDate,
        ...(institution ? { institution_name: institution } : {}),
      },
      { onSuccess: () => navigate(`/households/${householdId}/accounts`) },
    );
  }

  return (
    <div className="max-w-md">
      <PageTitle>{t("accounts.add")}</PageTitle>

      <Panel>
      <form onSubmit={onSubmit} noValidate>
        <Field label={t("account.name")} htmlFor="account-name" error={violation("name")}>
          <input
            id="account-name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("accountKind.checking")}
            autoFocus
            className={inputClass}
          />
        </Field>

        <Field label={t("account.kind")} htmlFor="account-kind" error={violation("kind")}>
          <select
            id="account-kind"
            name="kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as AccountKind)}
            className={inputClass}
          >
            {ACCOUNT_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {t(k.key)}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={`${t("accounts.openingBalance")} (${currency})`}
          htmlFor="account-opening"
          hint={t("account.openingHint")}
          error={amountError ?? violation("opening_balance_minor")}
        >
          <input
            id="account-opening"
            name="opening_balance_minor"
            value={opening}
            onChange={(e) => setOpening(e.target.value)}
            inputMode="decimal"
            className={`${inputClass} num`}
          />
        </Field>

        <Field
          label={t("account.openingDate")}
          htmlFor="account-opening-date"
          hint={t("account.openingDateHint")}
          error={violation("opening_date")}
        >
          <input
            id="account-opening-date"
            name="opening_date"
            type="date"
            value={openingDate}
            onChange={(e) => setOpeningDate(e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label={`${t("account.bank")} (${t("common.optional")})`} htmlFor="account-institution">
          <input
            id="account-institution"
            name="institution_name"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            placeholder="Sparkasse"
            className={inputClass}
          />
        </Field>

        <button type="submit" disabled={create.isPending} className={`${buttonClass} mt-6`}>
          {create.isPending ? t("onboarding.creating") : t("accounts.add")}
        </button>
      </form>
      </Panel>

      <ErrorNote error={create.error} />
    </div>
  );
}
